'use strict';
/* ==========================================================================
   core-local/scale.js — "Balance It": the two-step-equation model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/scalecheck.js, tools/contentcheck.js) and in
   the page (window.Scale). Registers itself with the question bank as `scale` when core/bank.js is loaded first
   (lesson.json scripts order).

   EXACT ARITHMETIC: every number is a reduced fraction {n, d} of integers (d > 0). A decimal (1.8, 0.75) becomes a
   fraction through its decimal string (9/5, 3/4), never through float division. Content expressions over params are
   evaluated EXACTLY by a small + − × ÷ ( ) evaluator (Scale.exact); anything else falls back to the shared bank's
   evaluator and the float is turned back into its small-denominator fraction.

   ---- fractions ----
   Scale.frac(v)                → {n, d}  from an int, a number (exact via its decimal string), "3/4", "−1.8", {n, d}
   Scale.add / sub / mul / div / neg / abs / eq / cmp / sign / isInt / isZero / toNumber / terminates
   Scale.num(q, style)          → "12", "−12", "7/2", "−7/2"; style 'dec': "1.8", "−0.75" when the decimal terminates
   Scale.parse(typed)           → {n, d} | null   ("−7/2", "-3.5", "35/3", "0.75", ".5", "12"; a/b with b ≠ 0)
   ---- equations (data) ----
   eq = {form: 'px+q=r' | 'p(x+q)=r', p, q, r, v: 'x', flip: false, lead: 'p' | 'q', style: 'frac' | 'dec'}
        p, q, r: fractions (Scale.mkEq(o) normalizes numbers / strings / expressions); q < 0 is written "− |q|"; flip = written
        r = px + q; lead 'q' writes px+q=r as "5 − 3h = −7"; style 'dec' prints non-integers as decimals (1.8).
   Scale.mkEq(o, params?)       → a normalized eq (p, q, r may be numbers, "a/b" or expressions over params)
   Scale.text(eq)               → "2x + 6 = 20", "3(t − 2) = −12", "−11 = 3x + 4", "x + 3 = 10", "−x + 4 = 1",
                                   "1.8C + 32 = −4", "(2/3)x − 7 = 7/9", "5 − 3h = −7"   (minus sign U+2212)
   Scale.side(eq)               → the variable side's text ("2x + 6")
   Scale.solve(eq)              → the exact solution {n, d}
   Scale.value(eq, x)           → the variable side's value at x (exact)
   Scale.check(eq, x)           → {left, right, ok, text: "Check: 2 × 7 + 6 = 20" | "... = 14, not 20"}
                                   (left/right: the WRITTEN sides' values; for a flipped eq the number is on the left)
   Scale.equivalent(a, b)       → same solution
   ---- worked lines ----
   Scale.METHODS                → ['undo', 'divideFirst', 'distribute']
   Scale.steps(eq, method)      → [{i, op, by, act, property, eq, text, value, pre, post}] + the last line
                                   {op: 'check', text, property: 'substitution', ok, left, right}.
                                   op: sub | add | div | mul | dist. value = the number a student types for the line
                                   (the new constant side; for dist the new constant |p·q|), shown as pre + [ ] + post.
                                   undo: add/subtract q, then divide by p (bags: divide first); divideFirst: divide every
                                   part by p, then subtract; distribute: p(x + q) → px + pq, then undo (on px + q = r it
                                   is undo). A non-integer
                                   fraction p (style frac) is undone by multiplying by its reciprocal.
   Scale.PROPS                  → op → "subtraction property of equality", ... "distributive property", "substitution"
   ---- the balance (honest for positive weights) ----
   Scale.canHold(eq)            → p a positive whole number (boxes or bags), q a whole number ≥ 0, r a positive whole
                                   number, and the solution > 0. False → the scene shows the "can't weigh negatives" sticky.
   Scale.contents(eq)           → {left: {boxes, bags, bagWeights, weights}, right: {...}} in WRITTEN order
   Scale.pans(eq, x)            → {left, right} total ounces (numbers) in written order
   Scale.tilt(L, R)             → beam angle in degrees = 12 × tanh((L − R) / 4): > 0 = the LEFT pan is lower, < 0 = the
                                   RIGHT pan is lower, exactly 0 when L = R. (Canvas: rotate the beam by −tilt·π/180.)
   Scale.moves(eq)              → [{id, label, enabled}] for 'sub1', 'sub5', 'split', 'unpack', 'left1' (the trap)
   Scale.move(eq, id)           → {ok, id, eq (the new balanced eq; unchanged for the trap / a disabled move), line
                                   ({act, property, text, eq}), pans, tilt, trap, solved}
   ---- Red Pen cheats (Jay's homework) ----
   Scale.CHEATS                 → ['oneSide', 'partDivide', 'partDistribute', 'signSlip', 'wrongInverse', 'moveNoSign']
   Scale.cheat(name, eq, method)→ {cheat, bad, lines (+ check), x (Jay's answer), honest (the honest lines)} or null
                                   when the cheat doesn't apply. ONE wrong line (index bad); every later line is honestly
                                   computed from the wrong one; the last line is the (failing) check.
                                   oneSide: the step done to the variable side only; partDivide: px + q → x + q (only
                                   the x term divided); partDistribute: p(x + q) → px + q; signSlip: the first negative
                                   number written without its sign; wrongInverse: px = r → x = r − p; moveNoSign: the
                                   constant moved across without changing sign (r + q instead of r − q).
   Scale.oneSide(eq, m) / partDivide / partDistribute / signSlip / wrongInverse / moveNoSign  (= cheat(name, …))
   Scale.wrongLine(name, before, step) → the wrong eq a misconception writes for this step (null if n/a); names: the
                                   six cheats + 'distNum' (p(x + q) → x + pq) + 'bagPart' (p(x + q) ÷ p → x + q/p)
   Scale.why(name, before, step, honest) → the red-pen remediation ("That was done to one side only. It should say ...")
   ---- pools (content/bank.pen.json, content/bank.scale.json) ----
   Scale.exact(expr, params)    → {n, d}   (derived params are recomputed exactly: Scale.exactParams(item, params))
   Scale.eqOf(item, params)     → the item's eq (item.eq's fields are expressions over the params)
   Scale.card(item, params)     → a Red Pen card: {id, title, eq, text, v, method, cheat, bad, lines, check, answer,
                                   solution, fix: {line, honest, wrong, other, otherName, property, act, whyWrong,
                                   whyOther} | null}
   Scale.itemSteps(item, params)→ The Scale's steps: [{i, type, text, hint, format, last, value, answerText, pre, post,
                                   property, act, lineText | options, correct}] (numpad values from the model)
   Scale.checkStep(step, typed) → numpad: exact (any equivalent fraction, or a decimal when it is exact); choice: index
   Scale.format(q, fmt)         → 'int' | 'num' (decimal when it terminates, else a/b) | 'frac' (a/b)
   Scale.fill(text, params)     → Bank.fill with {eq} / {ans} placeholders
   Scale.pool(json)             → a copy the shared core/bank.js accepts (the games pass THIS to Bank.attempt)
   Scale.byLevel(instances)     → instances sorted easiest first (item.slot, then item.level; stable)
   Scale.VIDEO                  → the video's equations and Jay's cheats (s1–s5)
   ========================================================================== */
(function (root) {
  /* ---------------- exact fractions ---------------- */
  const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
  function mk(n, d) {
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d) || d === 0) throw new Error(`scale: bad fraction ${n}/${d}`);
    if (d < 0) { n = -n; d = -d; }
    const k = gcd(n, d);
    return { n: n / k + 0, d: d / k };
  }
  function fromDecimalString(s) {
    s = String(s).trim().replace(/[−–]/g, '-');
    const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(s);
    if (!m || (m[2] === '' && !m[3])) return null;
    const fp = m[3] || '', d = Math.pow(10, fp.length), n = (+m[2] || 0) * d + (+fp || 0);
    return mk(m[1] ? -n : n, d);
  }
  function frac(v) {
    if (v && typeof v === 'object' && 'n' in v && 'd' in v) return mk(+v.n, +v.d);
    if (typeof v === 'string') {
      const s = v.trim().replace(/[−–]/g, '-'), m = /^(-?)(\d+)\s*\/\s*(\d+)$/.exec(s);
      if (m) return mk((m[1] ? -1 : 1) * +m[2], +m[3]);
      const q = fromDecimalString(s);
      if (q) return q;
      throw new Error(`scale: "${v}" is not a number`);
    }
    if (typeof v !== 'number' || !isFinite(v)) throw new Error(`scale: ${v} is not a finite number`);
    if (Number.isInteger(v)) return mk(v, 1);
    const s = String(v);
    if (/e/i.test(s)) throw new Error(`scale: ${v} is too small or large for an exact fraction`);
    return fromDecimalString(s);
  }
  const F = frac;
  const add = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d + b.n * a.d, a.d * b.d); };
  const sub = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.d - b.n * a.d, a.d * b.d); };
  const mul = (a, b) => { a = F(a); b = F(b); return mk(a.n * b.n, a.d * b.d); };
  const div = (a, b) => { a = F(a); b = F(b); if (b.n === 0) throw new Error('scale: ÷ 0'); return mk(a.n * b.d, a.d * b.n); };
  const neg = a => { a = F(a); return mk(-a.n, a.d); };
  const abs = a => { a = F(a); return mk(Math.abs(a.n), a.d); };
  const eq = (a, b) => { if (a == null || b == null) return a == null && b == null; a = F(a); b = F(b); return a.n === b.n && a.d === b.d; };
  const cmp = (a, b) => { const c = sub(a, b); return Math.sign(c.n); };
  const sign = a => Math.sign(F(a).n);
  const isInt = a => F(a).d === 1;
  const isZero = a => F(a).n === 0;
  const toNumber = a => { a = F(a); return a.n / a.d; };
  function terminates(q) { let d = F(q).d; while (d % 2 === 0) d /= 2; while (d % 5 === 0) d /= 5; return d === 1; }
  const ONE = mk(1, 1), ZERO = mk(0, 1);

  /* ---------------- number text ---------------- */
  const minus = s => String(s).replace(/^-/, '−');
  function decText(q) {                                   // exact decimal of a terminating fraction
    q = F(q);
    const neg_ = q.n < 0, n = Math.abs(q.n), ip = Math.floor(n / q.d);
    let r = n % q.d, fp = '';
    for (let i = 0; r && i < 16; i++) { r *= 10; fp += Math.floor(r / q.d); r %= q.d; }
    return (neg_ ? '−' : '') + ip + (fp ? '.' + fp : '');
  }
  /* "12", "−12", "7/2", "−7/2"; style 'dec' → "1.8" when the decimal terminates */
  function num(q, style) {
    q = F(q);
    if (q.d === 1) return minus(String(q.n));
    if (style === 'dec' && terminates(q)) return decText(q);
    return minus(q.n + '/' + q.d);
  }
  const plain = (q, style) => isInt(q) || (style === 'dec' && terminates(q));   // prints without parentheses
  /* a coefficient in front of the variable: x, −x, 3x, −3x, 1.8C, (2/3)x, −(2/3)x */
  function coef(p, v, style) {
    p = F(p);
    if (eq(p, ONE)) return v;
    if (eq(p, neg(ONE))) return '−' + v;
    const a = abs(p), s = plain(a, style) ? num(a, style) : '(' + num(a, style) + ')';
    return (p.n < 0 ? '−' : '') + s + v;
  }
  /* a number as a factor in a product: 7, (−5), (35/3), 1.8, (−0.5) */
  const factor = (q, style) => (F(q).n < 0 || !plain(q, style) ? '(' + num(q, style) + ')' : num(q, style));
  const pm = (a, sgn, b) => `${a} ${sgn < 0 ? '−' : '+'} ${b}`;

  /* ---------------- equations ---------------- */
  const FORMS = ['px+q=r', 'p(x+q)=r'];
  const PROPS = {
    sub: 'subtraction property of equality', add: 'addition property of equality',
    div: 'division property of equality', mul: 'multiplication property of equality',
    dist: 'distributive property', check: 'substitution',
  };
  const METHODS = ['undo', 'divideFirst', 'distribute'];
  function normEq(o) {
    if (!FORMS.includes(o.form)) throw new Error(`scale: unknown form "${o.form}"`);
    const e = { form: o.form, p: F(o.p), q: F(o.q == null ? 0 : o.q), r: F(o.r), v: o.v || 'x', flip: !!o.flip, lead: o.lead === 'q' ? 'q' : 'p', style: o.style === 'dec' ? 'dec' : 'frac' };
    if (isZero(e.p)) throw new Error('scale: p = 0 is not an equation in x');
    // 1(x + q) is x + q, p(x + 0) is px: no bag left to open
    if (e.form === 'p(x+q)=r' && (eq(e.p, ONE) || isZero(e.q))) { e.form = 'px+q=r'; }
    return e;
  }
  const withEq = (e, ch) => normEq({ ...e, ...ch });
  function side(e) {
    const { p, q, v, style } = e;
    if (e.form === 'p(x+q)=r') {
      if (isZero(q)) return coef(p, v, style);
      const inner = pm(v, sign(q), num(abs(q), style));
      const pc = eq(p, ONE) ? '' : eq(p, neg(ONE)) ? '−' : (p.n < 0 ? '−' : '') + (plain(abs(p), style) ? num(abs(p), style) : '(' + num(abs(p), style) + ')');
      return `${pc}(${inner})`;
    }
    if (isZero(q)) return coef(p, v, style);
    if (e.lead === 'q') return pm(num(q, style), sign(p), coef(abs(p), v, style));
    return pm(coef(p, v, style), sign(q), num(abs(q), style));
  }
  function text(e) { e = e.form ? normEq(e) : e; return e.flip ? `${num(e.r, e.style)} = ${side(e)}` : `${side(e)} = ${num(e.r, e.style)}`; }
  function value(e, x) { return e.form === 'p(x+q)=r' ? mul(e.p, add(x, e.q)) : add(mul(e.p, x), e.q); }
  function solve(e) { e = normEq(e); return e.form === 'p(x+q)=r' ? sub(div(e.r, e.p), e.q) : div(sub(e.r, e.q), e.p); }
  const equivalent = (a, b) => eq(solve(a), solve(b));
  const solved = e => e.form === 'px+q=r' && eq(e.p, ONE) && isZero(e.q);
  /* the substitution text of the variable side at x: "2 × 7 + 6", "3 × (−5) + 4", "3 × (6 + 2)", "5 − 3 × 4" */
  function subst(e, x) {
    const { p, q, style } = e, xs = factor(x, style);
    if (e.form === 'p(x+q)=r') {
      const inner = isZero(q) ? num(x, style) : pm(num(x, style), sign(q), num(abs(q), style));
      return `${eq(p, ONE) ? '' : eq(p, neg(ONE)) ? '−' : factor(p, style) + ' × '}(${inner})`;
    }
    const term = a => (eq(a, ONE) ? num(x, style) : `${factor(a, style)} × ${xs}`);
    if (isZero(q)) return eq(p, neg(ONE)) ? `−${xs}` : term(p);
    if (e.lead === 'q') return pm(num(q, style), sign(p), term(abs(p)));
    const first = eq(p, neg(ONE)) ? `−${xs}` : term(p);
    return pm(first, sign(q), num(abs(q), style));
  }
  function check(e, x) {
    e = normEq(e); x = F(x);
    const val = value(e, x), ok = eq(val, e.r);
    const txt = `Check: ${subst(e, x)} = ${num(val, e.style)}${ok ? '' : ', not ' + num(e.r, e.style)}`;
    return { left: e.flip ? e.r : val, right: e.flip ? val : e.r, value: val, ok, text: txt, property: PROPS.check };
  }

  /* ---------------- the honest steps ---------------- */
  const fracP = e => !isInt(e.p) && !(e.style === 'dec' && terminates(e.p));   // undone by × the reciprocal
  /* one step of op on e → {op, by, eq, every} (every: dividing every part of a two-term side) */
  function applyOp(e, op) {
    if (op === 'sub' || op === 'add') return { op, by: abs(e.q), eq: withEq(e, { form: 'px+q=r', q: 0, r: sub(e.r, e.q) }) };
    if (op === 'dist') return { op, by: e.p, eq: withEq(e, { form: 'px+q=r', q: mul(e.p, e.q) }) };
    // div / mul by 1/p
    const by = op === 'mul' ? div(ONE, e.p) : e.p;
    if (e.form === 'p(x+q)=r') return { op, by, eq: withEq(e, { form: 'px+q=r', p: 1, r: div(e.r, e.p) }), bags: true };
    return { op, by, eq: withEq(e, { p: 1, q: div(e.q, e.p), r: div(e.r, e.p) }), every: !isZero(e.q) };
  }
  const divOp = e => (fracP(e) ? 'mul' : 'div');
  const addOp = e => (sign(e.q) > 0 ? 'sub' : 'add');
  /* the next honest op for method m, or null when solved */
  function nextOp(e, m) {
    if (solved(e)) return null;
    if (e.form === 'p(x+q)=r') return m === 'distribute' ? 'dist' : divOp(e);
    if (m === 'divideFirst' && !eq(e.p, ONE)) return divOp(e);
    if (!isZero(e.q)) return addOp(e);
    return divOp(e);
  }
  function actText(before, st) {
    const s = before.style;
    if (st.op === 'sub') return `Subtract ${num(st.by, s)} from both sides`;
    if (st.op === 'add') return `Add ${num(st.by, s)} to both sides`;
    if (st.op === 'dist') return `Distribute the ${num(st.by, s)}`;
    if (st.op === 'mul') return `Multiply both sides by ${num(st.by, s)}`;
    return st.every ? `Divide every part of both sides by ${num(st.by, s)}` : `Divide both sides by ${num(st.by, s)}`;
  }
  /* the typed value of a line and its blank: pre + [value] + post */
  function blank(before, st, e) {
    if (st.op === 'dist') {
      const c = e.q, head = pm(coef(e.p, e.v, e.style), sign(c), '').trimEnd() + ' ';
      return e.flip ? { value: abs(c), pre: `${num(e.r, e.style)} = ${head}`, post: '' } : { value: abs(c), pre: head, post: ` = ${num(e.r, e.style)}` };
    }
    return e.flip ? { value: e.r, pre: '', post: ` = ${side(e)}` } : { value: e.r, pre: `${side(e)} = `, post: '' };
  }
  function line(before, st, i) {
    const e = st.eq, b = blank(before, st, e);
    return { i, op: st.op, by: st.by, every: !!st.every, bags: !!st.bags, act: actText(before, st), property: PROPS[st.op], eq: e, text: text(e), value: b.value, pre: b.pre, post: b.post, before };
  }
  /* honest lines from e by method m (no check line) */
  function solveLines(e, m, from = 0) {
    const out = [];
    let cur = e;
    for (let k = 0; k < 8; k++) {
      const op = nextOp(cur, m);
      if (!op) return out;
      const st = applyOp(cur, op);
      out.push(line(cur, st, from + out.length));
      cur = st.eq;
    }
    throw new Error(`scale: ${text(e)} did not solve in 8 steps`);
  }
  function steps(e, method = 'undo') {
    e = normEq(e);
    if (!METHODS.includes(method)) throw new Error(`scale: unknown method "${method}"`);
    const L = solveLines(e, method);
    const x = solve(e), c = check(e, x);
    return L.concat([{ i: L.length, op: 'check', ...c, eq: e }]);
  }

  /* ---------------- misconceptions: the wrong line a step turns into ---------------- */
  const CHEATS = ['oneSide', 'partDivide', 'partDistribute', 'signSlip', 'wrongInverse', 'moveNoSign'];
  const EXTRA = ['distNum', 'bagPart'];
  /* the wrong eq misconception `name` writes instead of honest step st (from `before`); null when it doesn't apply */
  function wrongLine(name, before, st) {
    const b = before, h = st.eq;
    switch (name) {
      case 'oneSide':                                     // the variable side changed, the number side not
        if (st.op === 'dist') return null;
        return withEq(h, { r: b.r });
      case 'moveNoSign':
        if (st.op !== 'sub' && st.op !== 'add') return null;
        return withEq(h, { r: add(b.r, b.q) });
      case 'partDivide':
        if (st.op !== 'div' || !st.every) return null;
        return withEq(h, { q: b.q });
      case 'wrongInverse':
        if ((st.op !== 'div' && st.op !== 'mul') || st.every || st.bags || b.form !== 'px+q=r' || !isZero(b.q)) return null;
        return withEq(h, { r: sub(b.r, b.p) });
      case 'partDistribute':
        if (st.op !== 'dist') return null;
        return withEq(h, { q: b.q });
      case 'distNum':
        if (st.op !== 'dist') return null;
        return withEq(h, { p: 1 });
      case 'bagPart':
        if (!st.bags) return null;
        return withEq(h, { q: div(b.q, b.p) });
      case 'signSlip': {
        if (sign(h.r) < 0) return withEq(h, { r: abs(h.r) });
        if (sign(h.q) < 0 && !eq(h.q, b.q)) return withEq(h, { q: abs(h.q) });
        return null;
      }
      default: throw new Error(`scale: unknown misconception "${name}"`);
    }
  }
  /* the red-pen remediation for `name` at this step (≤ ~110 characters) */
  function why(name, before, st, honestText) {
    const s = before.style, v = before.v;
    const reason = {
      oneSide: 'That was done to one side only',
      moveNoSign: `The ${num(abs(before.q), s)} crossed sides without changing its sign`,
      partDivide: 'Only part of a side got divided. Divide every part',
      wrongInverse: `${coef(before.p, v, s)} means ${num(before.p, s)} times ${v}: divide, don't subtract`,
      partDistribute: `The ${num(before.p, s)} multiplies both terms in the parentheses`,
      distNum: `The ${num(before.p, s)} multiplies the ${v} too`,
      bagPart: `The ${num(before.p, s)} multiplies the whole bag, so dividing leaves the ${num(abs(before.q), s)} whole`,
      signSlip: 'A negative sign got lost',
    }[name];
    if (!reason) throw new Error(`scale: no remediation for "${name}"`);
    return `${reason}. It should say ${honestText}.`;
  }
  /* the card with misconception `name` applied at the first step where it changes the answer */
  function cheat(name, e, method = 'undo') {
    e = normEq(e);
    if (!CHEATS.includes(name)) throw new Error(`scale: unknown cheat "${name}"`);
    const honest = steps(e, method), H = honest.slice(0, -1), x0 = solve(e);
    for (let k = 0; k < H.length; k++) {
      const st = H[k], w = wrongLine(name, st.before, st);
      if (!w || eq(solve(w), x0)) continue;
      const bad = { ...st, eq: w, text: text(w), ...blankOf(st, w), wrong: name };
      const rest = solveLines(w, method, k + 1);
      const x = rest.length ? solve(rest[rest.length - 1].eq) : solve(w);
      const c = check(e, x);
      return { cheat: name, bad: k, lines: H.slice(0, k).concat([bad], rest, [{ i: k + 1 + rest.length, op: 'check', ...c, eq: e }]), x, honest };
    }
    return null;
  }
  function blankOf(st, w) { const b = blank(st.before, st, w); return { value: b.value, pre: b.pre, post: b.post }; }

  /* ---------------- the balance ---------------- */
  const wholePos = q => isInt(q) && sign(q) > 0;
  function canHold(e) {
    e = normEq(e);
    return wholePos(e.p) && isInt(e.q) && sign(e.q) >= 0 && wholePos(e.r) && sign(solve(e)) > 0;
  }
  function contents(e) {
    e = normEq(e);
    const n = q => toNumber(q);
    const xs = e.form === 'p(x+q)=r' ? { boxes: 0, bags: n(e.p), bagWeights: n(e.q), weights: 0 } : { boxes: n(e.p), bags: 0, bagWeights: 0, weights: n(e.q) };
    const ns = { boxes: 0, bags: 0, bagWeights: 0, weights: n(e.r) };
    return e.flip ? { left: ns, right: xs } : { left: xs, right: ns };
  }
  function pans(e, x) {
    e = normEq(e);
    const xv = x == null ? solve(e) : F(x), s = toNumber(value(e, xv)), r = toNumber(e.r);
    return e.flip ? { left: r, right: s } : { left: s, right: r };
  }
  function tilt(L, R) {
    const l = typeof L === 'number' ? L : toNumber(L), r = typeof R === 'number' ? R : toNumber(R);
    if (l === r) return 0;
    return 12 * Math.tanh((l - r) / 4);
  }
  /* the moves The Scale offers on the balance (rounds 1–3) */
  function moveInfo(e) {
    const bags = e.form === 'p(x+q)=r', loose = bags ? ZERO : e.q;       // weights lying loose on the variable pan
    const p = e.p, r = e.r;
    return {
      sub1: { label: '− 1 from both', enabled: cmp(loose, 1) >= 0 && cmp(r, 1) >= 0 },
      sub5: { label: '− 5 from both', enabled: cmp(loose, 5) >= 0 && cmp(r, 5) >= 0 },
      split: { label: `split both into ${num(p)} groups`, enabled: wholePos(p) && cmp(p, 1) > 0 && isInt(div(r, p)) && (bags || isInt(div(e.q, p))) },
      unpack: { label: 'open the bags', enabled: bags },
      left1: { label: '− 1 from the box side only', enabled: cmp(loose, 1) >= 0 },
    };
  }
  function moves(e) { e = normEq(e); const I = moveInfo(e); return Object.keys(I).map(id => ({ id, label: I[id].label, enabled: I[id].enabled })); }
  function move(e, id) {
    e = normEq(e);
    const I = moveInfo(e)[id], x = solve(e);
    if (!I) throw new Error(`scale: unknown move "${id}"`);
    const out = { id, ok: false, eq: e, line: null, trap: id === 'left1', solved: solved(e) };
    if (!I.enabled) { out.pans = pans(e, x); out.tilt = tilt(out.pans.left, out.pans.right); return out; }
    if (id === 'left1') {
      const P = pans(e, x), L = e.flip ? P.left : P.left - 1, R = e.flip ? P.right - 1 : P.right;
      out.pans = { left: L, right: R }; out.tilt = tilt(L, R);
      out.line = { act: 'Take 1 from the box side only', property: 'not balanced: both sides must change', text: '', eq: e };
      return out;
    }
    let ne, act, prop;
    if (id === 'sub1' || id === 'sub5') { const k = id === 'sub1' ? 1 : 5; ne = withEq(e, { q: sub(e.q, k), r: sub(e.r, k) }); act = `Take ${k} from both sides`; prop = PROPS.sub; }
    else if (id === 'split') { const st = applyOp(e, 'div'); ne = st.eq; act = `Split both sides into ${num(e.p)} equal groups; keep one`; prop = PROPS.div; }
    else { ne = withEq(e, { form: 'px+q=r', q: mul(e.p, e.q) }); act = 'Open the bags'; prop = PROPS.dist; }
    out.ok = true; out.eq = ne; out.solved = solved(ne);
    out.line = { act, property: prop, text: text(ne), eq: ne };
    out.pans = pans(ne, x); out.tilt = tilt(out.pans.left, out.pans.right);
    return out;
  }

  /* ---------------- exact expressions over params ---------------- */
  function tokens(s) {
    const out = []; let i = 0; s = String(s);
    while (i < s.length) {
      const c = s[i];
      if (/\s/.test(c)) { i++; continue; }
      let m;
      if ((m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i)))) { out.push({ t: 'num', v: m[0] }); i += m[0].length; continue; }
      if ((m = /^[A-Za-z_$][\w$]*/.exec(s.slice(i)))) { out.push({ t: 'id', v: m[0] }); i += m[0].length; continue; }
      if ('+-*/()'.includes(c)) { out.push({ t: 'op', v: c }); i++; continue; }
      if (c === '−') { out.push({ t: 'op', v: '-' }); i++; continue; }
      return null;                                         // not a plain arithmetic expression
    }
    return out;
  }
  function exactPlain(expr, vals) {
    const tk = tokens(expr);
    if (!tk || !tk.length) return null;
    let i = 0;
    const peek = () => tk[i], take = () => tk[i++];
    function prim() {
      const k = take();
      if (!k) throw new Error('end');
      if (k.t === 'num') return F(k.v);
      if (k.t === 'id') {
        if (k.v === 'abs' && peek() && peek().v === '(') { take(); const a = sum(); if (!take() || tk[i - 1].v !== ')') throw new Error(')'); return abs(a); }
        if (!(k.v in vals)) throw new Error(`unknown ${k.v}`);
        return vals[k.v];
      }
      if (k.v === '(') { const a = sum(); const c = take(); if (!c || c.v !== ')') throw new Error(')'); return a; }
      if (k.v === '-') return neg(prim());
      if (k.v === '+') return prim();
      throw new Error(`unexpected ${k.v}`);
    }
    function prod() { let a = prim(); while (peek() && (peek().v === '*' || peek().v === '/')) { const o = take().v, b = prim(); a = o === '*' ? mul(a, b) : div(a, b); } return a; }
    function sum() { let a = prod(); while (peek() && (peek().v === '+' || peek().v === '-')) { const o = take().v, b = prod(); a = o === '+' ? add(a, b) : sub(a, b); } return a; }
    try { const v = sum(); return i === tk.length ? v : null; } catch (e) { if (/unknown|÷ 0/.test(e.message)) throw e; return null; }
  }
  /* exact value of a content expression over (exact) params; a number / "a/b" is taken as is */
  function exact(expr, params = {}) {
    if (typeof expr === 'number' || (expr && typeof expr === 'object')) return F(expr);
    const vals = {};
    for (const [k, v] of Object.entries(params)) if (typeof v === 'number' || (v && typeof v === 'object' && 'n' in v)) { try { vals[k] = F(v); } catch (e) { /* a float that isn't exact: not usable */ } }
    const s = String(expr).trim();
    if (/^-?\d+\s*\/\s*\d+$/.test(s)) return F(s);
    const q = exactPlain(s, vals);
    if (q) return q;
    // anything else: the shared evaluator (floats) → the small-denominator fraction it is
    const v = BANK().eval(s, Object.fromEntries(Object.entries(vals).map(([k, x]) => [k, toNumber(x)])));
    const r = BANK().rational(v);
    if (!r) throw new Error(`scale: "${expr}" = ${v} is not a small-denominator fraction`);
    return mk(r.n, r.d);
  }
  /* the item's params, exact: sampled numbers as fractions, derived ({expr}) params recomputed exactly, in order */
  function exactParams(item, params = {}) {
    const out = {};
    for (const [k, def] of Object.entries(item.params || {})) {
      if (def && typeof def === 'object' && typeof def.expr === 'string') out[k] = exact(def.expr, out);
      else if (params[k] != null) out[k] = F(params[k]);
    }
    for (const [k, v] of Object.entries(params)) if (!(k in out) && (typeof v === 'number' || (v && typeof v === 'object'))) { try { out[k] = F(v); } catch (e) { /* not exact (a float): skip */ } }
    return out;
  }
  function mkEq(o, params = {}) {
    const P = params && Object.values(params).some(v => v && typeof v === 'object') ? params : exactParams({}, params);
    const val = x => (x == null ? ZERO : exact(x, P));
    return normEq({ ...o, p: val(o.p), q: val(o.q), r: val(o.r) });
  }
  const eqOf = (item, params) => mkEq(item.eq, exactParams(item, params));

  /* ---------------- formats / typed answers ---------------- */
  function format(q, fmt) {
    q = F(q);
    if (fmt === 'int') { if (q.d !== 1) throw new Error(`scale: ${num(q)} is not a whole number (format int)`); return num(q); }
    if (fmt === 'num') return num(q, 'dec');
    return num(q);
  }
  function parse(typed) {
    if (typed == null) return null;
    if (typeof typed === 'number') { try { return F(typed); } catch (e) { return null; } }
    const s = String(typed).replace(/[\s,]/g, '').replace(/[−–]/g, '-');
    let m;
    if ((m = /^(-?)(\d+)\/(-?)(\d+)$/.exec(s))) { if (!+m[4]) return null; return mk(((m[1] ? -1 : 1) * (m[3] ? -1 : 1)) * +m[2], +m[4]); }
    if (/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return fromDecimalString(s);
    return null;
  }

  /* ---------------- the bank side ---------------- */
  function BANK() {
    let B = root.Bank;
    if (!B && typeof require === 'function') B = require('../../../shared/engine/v2/core/bank.js');
    if (!B) throw new Error('scale: core/bank.js is not loaded');
    if (!B.registered().includes('scale')) B.register('scale', Scale);
    return B;
  }
  /* the registry the generated {scale.…} calls read: REG[pool][k] = {item, names} (Scale.pool fills it) */
  const REG = {};
  const regItem = (pool, k) => { const r = REG[pool] && REG[pool][k]; if (!r) throw new Error(`scale: no item ${pool}#${k} (call Scale.pool first)`); return r; };
  const regParams = (r, vals) => Object.fromEntries(r.names.map((n, i) => [n, vals[i]]));
  /* {eq} → the equation, {ans} → the answer (placeholders in pool text) */
  function place(text, pool, k, names) {
    const call = what => `{scale.${what}('${pool}',${k}${names.length ? ',' + names.join(',') : ''})}`;
    return String(text).replace(/\{eq\}/g, call('e')).replace(/\{ans\}/g, call('a'));
  }
  function fill(text, params, item) {
    if (text == null) return '';
    let s = String(text);
    if (item && /\{(eq|ans)\}/.test(s)) {
      const e = eqOf(item, params);
      s = s.replace(/\{eq\}/g, text_(e)).replace(/\{ans\}/g, num(solve(e), e.style));
    }
    return BANK().fill(s, params);
  }
  const text_ = e => text(e);

  /* ---------------- Red Pen cards ---------------- */
  const ALTS = {                                           // the "other misconception" option, by step kind
    addsub: ['moveNoSign', 'oneSide', 'signSlip'],
    every: ['partDivide', 'oneSide', 'signSlip'],
    single: ['wrongInverse', 'oneSide', 'signSlip'],
    bags: ['bagPart', 'oneSide', 'signSlip'],
    dist: ['partDistribute', 'distNum', 'signSlip'],
  };
  const kindOf = st => (st.op === 'sub' || st.op === 'add' ? 'addsub' : st.op === 'dist' ? 'dist' : st.bags ? 'bags' : st.every ? 'every' : 'single');
  function card(item, params) {
    const e = eqOf(item, params), method = item.method || 'undo', name = item.cheat || 'legit';
    const honest = steps(e, method), x0 = solve(e);
    const base = { id: item.id, title: item.title || '', eq: e, text: text(e), v: e.v, method, cheat: name, level: item.level || 1, solution: x0 };
    if (name === 'legit') return { ...base, bad: -1, lines: honest, check: honest[honest.length - 1], answer: x0, fix: null, honest };
    const c = cheat(name, e, method);
    if (!c) throw new Error(`scale.card: ${item.id}: the cheat "${name}" doesn't apply to ${text(e)} (${method})`);
    const hs = honest[c.bad], bl = c.lines[c.bad];
    let other = null, otherName = null;
    for (const n of ALTS[kindOf(hs)]) {
      if (n === name) continue;
      const w = wrongLine(n, hs.before, hs);
      if (!w) continue;
      const t = text(w);
      if (t === hs.text || t === bl.text) continue;
      other = t; otherName = n; break;
    }
    if (!other) throw new Error(`scale.card: ${item.id}: no third option for line ${c.bad + 1}`);
    return { ...base, bad: c.bad, lines: c.lines, check: c.lines[c.lines.length - 1], answer: c.x, honest,
      fix: { line: c.bad, honest: hs.text, wrong: bl.text, other, otherName, property: hs.property, act: hs.act,
        whyWrong: why(name, hs.before, hs, hs.text), whyOther: why(otherName, hs.before, hs, hs.text) } };
  }
  /* the generated fields of a pen card (Scale.pool wires them into the bank copy): 0 q, 1–3 options, 4 explain, 5–6 why */
  function penField(C, f) {
    if (C.bad < 0) {
      if (f === 4) return `Every line keeps both sides equal. ${C.check.text}.`;
      return '';
    }
    const k = C.bad + 1, fx = C.fix;
    return [
      `Line ${k} is wrong. What should it say?`, fx.honest, fx.wrong, fx.other,
      `Line ${k} should say ${fx.honest} (${fx.property}).`, fx.whyWrong, fx.whyOther,
    ][f];
  }

  /* ---------------- The Scale's steps ---------------- */
  function itemSteps(item, params) {
    const P = exactParams(item, params), e = mkEq(item.eq, P), method = item.method || 'undo';
    const L = solveLines(e, method);
    const S = item.steps || [];
    let k = 0;
    return S.map((s, i) => {
      const o = { i, type: s.type, text: fill(s.text, params, item), hint: s.hint ? fill(s.hint, params, item) : '', format: s.format || null, last: i === S.length - 1 };
      if (s.type === 'numpad') {
        const ln = L[k++];
        if (!ln) throw new Error(`scale.itemSteps: ${item.id}: step ${i + 1} has no model line (${text(e)} solves in ${L.length})`);
        o.value = ln.value; o.answerText = format(ln.value, s.format || 'frac'); o.pre = ln.pre; o.post = ln.post;
        o.property = ln.property; o.act = ln.act; o.lineText = ln.text; o.op = ln.op;
      } else if (s.type === 'choice') {
        o.options = (s.options || []).map(op => (typeof op === 'string' ? fill(op, params, item) : text(mkEq({ ...item.eq, ...op }, P))));
        o.correct = s.correct; o.answerText = o.options[o.correct];
      } else throw new Error(`scale.itemSteps: ${item.id} step ${i + 1}: unknown type "${s.type}"`);
      return o;
    });
  }
  function checkStep(step, typed) {
    if (step.type === 'choice') return typed === step.correct;
    if (step.type === 'numpad') { const q = parse(typed); return !!q && eq(q, step.value); }
    return false;
  }
  function scaleExplain(item, params) {
    const e = eqOf(item, params), L = solveLines(e, item.method || 'undo');
    const t = L.map(l => l.text);
    return t.length > 1 ? `${t.slice(0, -1).join(', ')}, so ${t[t.length - 1]}.` : `${t[0]}.`;
  }

  /* ---------------- pools the shared core/bank.js accepts ----------------
     Scale.pool(json) → a copy with:
       Red Pen (pool "pen"): a cheat card gets q ("Line k is wrong. What should it say?"), 3 options (the honest line,
         the cheat's line, another misconception's line), correct 0, why, explain — all as {scale.f(...)} calls that
         build the card from the params (so every word comes from the model); a legit card keeps its own q / options /
         why and gets a generated explain when it has none. Both are "scenario" items for the bank (params + cheat).
       The Scale (pool "scale"): answer "numpad", formula = the last numpad step's formula, format = its format (a format
         the shared bank doesn't know yet becomes dec2: the game checks with Scale.checkStep anyway), explain = the
         model's lines; {eq} / {ans} in text → {scale.e(...)} / {scale.a(...)}; every param also gets an _a<name> =
         abs(param) so a negative number printed as "−11" is still "a number from a param".
     Harmless if the shared bank later learns more formats: nothing is rewritten that it already accepts. */
  function pool(json) {
    const out = JSON.parse(JSON.stringify(json)), name = out.pool || 'pool';
    REG[name] = [];
    (out.items || []).forEach((it, k) => {
      const src = (json.items || [])[k], names = Object.keys(it.params || {}), changed = [];
      REG[name][k] = { item: src, names };
      const call = (f, extra) => `{scale.${f}('${name}',${k}${extra != null ? ',' + extra : ''}${names.length ? ',' + names.join(',') : ''})}`;
      for (const f of ['q', 'explain']) if (typeof it[f] === 'string' && /\{(eq|ans)\}/.test(it[f])) { it[f] = place(it[f], name, k, names); changed.push(`{eq}/{ans} in ${f}`); }
      if (Array.isArray(it.options)) it.options = it.options.map(o => (typeof o === 'string' ? place(o, name, k, names) : o));
      if (it.why) for (const w of Object.keys(it.why)) it.why[w] = place(it.why[w], name, k, names);
      if (it.cheat !== undefined) {                        // a Red Pen card
        if (it.cheat !== 'legit') {
          it.q = call('f', 0); it.options = [call('f', 1), call('f', 2), call('f', 3)]; it.correct = 0;
          it.why = { 1: call('f', 5), 2: call('f', 6) }; it.explain = call('f', 4);
          changed.push('cheat card: q, options, why, explain from the model');
        } else if (it.explain === undefined) { it.explain = call('f', 4); changed.push('legit card: explain from the model'); }
        delete it.steps;
      } else if (Array.isArray(it.steps)) {               // a Scale item
        const numeric = it.steps.filter(s => s.type === 'numpad'), last = numeric[numeric.length - 1];
        it.answer = 'numpad';
        it.formula = last ? String(last.formula) : '0';
        const B = BANK();
        const fmt = last && last.format ? last.format : 'frac';
        it.format = B.FORMATS.includes(fmt) ? fmt : 'dec2';
        if (it.explain === undefined) { it.explain = call('x'); changed.push('explain from the model'); }
        for (const n of names) if (!/^_/.test(n)) it.params['_a' + n] = { expr: `abs(${n})` };
        changed.push(`numpad: formula = the last numpad step, format ${it.format}; _a<param> = abs(param)`);
      }
      if (changed.length) it._normalized = changed;
    });
    return out;
  }
  /* the generated calls: e(pool, k, …params) eq text; a(…) the answer; f(pool, k, field, …) a pen card field;
     x(pool, k, …) a Scale item's explain */
  function e_(pool, k, ...vals) { const r = regItem(pool, k); return text(eqOf(r.item, regParams(r, vals))); }
  function a_(pool, k, ...vals) { const r = regItem(pool, k), E = eqOf(r.item, regParams(r, vals)); return num(solve(E), E.style); }
  function f_(pool, k, f, ...vals) { const r = regItem(pool, k); return penField(card(r.item, regParams(r, vals)), f); }
  function x_(pool, k, ...vals) { const r = regItem(pool, k); return scaleExplain(r.item, regParams(r, vals)); }
  const source = (pool, k) => regItem(pool, k).item;
  function byLevel(instances) {
    const key = i => { const it = (i && i.item) || {}; return [it.slot != null ? it.slot : 99, it.level != null ? it.level : 99]; };
    return instances.map((x, i) => ({ x, i, k: key(x) })).sort((a, b) => a.k[0] - b.k[0] || a.k[1] - b.k[1] || a.i - b.i).map(o => o.x);
  }

  /* ---------------- the video (DESIGN.md §5.1) ---------------- */
  const VIDEO = {
    s1: { eq: { form: 'px+q=r', p: 2, q: 6, r: 20, v: 'x' }, cheat: 'partDivide', method: 'divideFirst' },     // socks: x = 7; Jay's x = 4
    s2: { eq: { form: 'px+q=r', p: 3, q: 5, r: 29, v: 'x' }, cheat: 'oneSide', method: 'undo' },               // mugs: x = 8; tilt(24, 29) < 0
    s3: { eq: { form: 'p(x+q)=r', p: 3, q: 2, r: 24, v: 'x' }, cheat: 'partDistribute', method: 'distribute' }, // gift bags: x = 6; Jay's 22/3
    s4: { eq: { form: 'px+q=r', p: 1.8, q: 32, r: -4, v: 'C', style: 'dec' }, cheat: 'signSlip', method: 'undo' }, // °F → °C: −20; can't hold
    s5: { eq: { form: 'px+q=r', p: 1, q: 0, r: 7, v: 'x' } },                                                 // a box and seven weights, level
  };

  const Scale = {
    frac, add, sub, mul, div, neg, abs, eq, cmp, sign, isInt, isZero, toNumber, terminates, num, decText, parse, format,
    FORMS, METHODS, PROPS, mkEq, normEq, text, side, solve, value, check, equivalent, solved, steps, subst,
    canHold, contents, pans, tilt, moves, move,
    CHEATS, EXTRA, cheat, wrongLine, why, ALTS,
    exact, exactParams, eqOf, card, penField, itemSteps, checkStep, scaleExplain, fill, pool, source, byLevel,
    e: e_, a: a_, f: f_, x: x_,
    VIDEO,
  };
  for (const n of CHEATS) Scale[n] = (e, m) => cheat(n, e, m);
  root.Scale = Scale;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('scale', Scale);
  if (typeof module !== 'undefined' && module.exports) module.exports = Scale;
})(typeof window !== 'undefined' ? window : globalThis);
